// ShareStatusModal — the "Share your status" affordance
// (AppDevPromptsMngmnt/handoffs/00-foundation.md §D, 2026-06-22).
//
// The shareable status badge (a 1200x630 social card: kite + Land Health
// score + trajectory + carbon + disclosure) is PRODUCED AND PUSHED by
// ReportBuilder — the app does not render or recompute it. This modal only
// DISPLAYS the pushed image and offers the three share interactions from the
// RB UX badge modal: download PNG, copy link, and the native share sheet.
//
// The badge image is resolved on open as a short-lived (15 min) signed URL via
// the report-download pipeline (api.js → getStatusBadgeUrl). A 404 / missing
// file means no badge has been published for the property yet, which we show
// as a friendly empty state rather than an error.
//
// Honest-claims note (per the handoff): the badge already carries its own
// honest wording. We add NO "verified / certified" framing around it here.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Share2, X, AlertCircle } from 'lucide-react';
import { getStatusBadgeUrl } from '../api';
import ShareStatusActions from './ShareStatusActions';
import { EcointelLottieMark } from './EcoIntelLoader';

export default function ShareStatusModal({ open, farmId, year, propertyName, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [badgeUrl, setBadgeUrl] = useState(null);

  // Guards against a stale resolve clobbering state after a fast close/reopen.
  const reqIdRef = useRef(0);

  // Resolve the badge signed URL each time the modal opens.
  useEffect(() => {
    if (!open || !farmId) return undefined;
    const reqId = ++reqIdRef.current;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    setBadgeUrl(null);
    (async () => {
      try {
        const resp = await getStatusBadgeUrl(farmId, year);
        if (cancelled || reqIdRef.current !== reqId) return;
        if (!resp?.url) throw new Error('No badge URL was returned.');
        setBadgeUrl(resp.url);
      } catch (e) {
        if (cancelled || reqIdRef.current !== reqId) return;
        // 404s mean "nothing published yet" — treat as an empty state, not an
        // error. Everything else surfaces a retryable message.
        const status = e?.status;
        if (status === 404) {
          setUnavailable(true);
        } else {
          console.error('[ShareStatusModal] badge resolve failed', e);
          setError(
            status === 503
              ? (e?.message || 'The share service is temporarily unavailable. Please retry.')
              : (e?.message || 'Could not load your status badge.')
          );
        }
      } finally {
        if (!cancelled && reqIdRef.current === reqId) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, farmId, year]);

  // Esc closes; lock background scroll while open.
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[#043132]/60 backdrop-blur-sm p-3 sm:p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Share your status"
    >
      <div className="flex w-full max-w-[640px] max-h-[92vh] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-3.5">
          <Share2 size={18} className="shrink-0 text-[#043132]" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-800 leading-tight">Share your status</div>
            {propertyName ? (
              <div className="truncate text-xs text-slate-500" title={propertyName}>{propertyName}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            title="Close"
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-300 text-slate-600 transition-colors hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {error ? (
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Couldn’t load your status badge</div>
                <div className="mt-0.5 text-xs">{error}</div>
              </div>
            </div>
          ) : unavailable ? (
            <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-slate-400" />
              <div>
                <div className="font-semibold text-slate-700">No status badge yet</div>
                <div className="mt-0.5 text-xs">
                  A shareable status badge has not been published for this property yet.
                  It will appear here once the EcoIntel team has produced it.
                </div>
              </div>
            </div>
          ) : loading || !badgeUrl ? (
            <div className="flex aspect-[1200/630] w-full items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500">
              <EcointelLottieMark size={22} className="mr-2" />
              <span className="text-sm">Loading your status badge…</span>
            </div>
          ) : (
            <>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {/* The pushed badge image, shown at its native 1200x630 ratio. */}
                <img
                  src={badgeUrl}
                  alt={propertyName ? `${propertyName} status badge` : 'Land status badge'}
                  className="block w-full"
                  style={{ aspectRatio: '1200 / 630', objectFit: 'contain' }}
                />
              </div>

              <ShareStatusActions
                badgeUrl={badgeUrl}
                propertyName={propertyName}
                className="mt-4"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
